import { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { getAuth, signOut } from 'firebase/auth';

import { Loader } from '@flintmint/web-shared/components/loader';

import { capture, reset } from '@/analytics';
import { getAuth0RedirectUri } from '@/utils/get-auth0-redirect-uri';
import { localDataService, PENDING_DATABASE_DELETION_KEY } from '@/providers/rxdb/local-data-service';
import { tabSyncService } from '@/services/tab-sync-service';
import { getFirebaseApp } from '@/services/firebase-service';
import { clearEditorContentCaches } from '@/utils/editor-utils';
import { withTimeout } from '@/utils/promise-utils';
import { UnsyncedChangesLogoutModal } from '@/components/save-before-logout-modal';

import { appRoutes } from '@/constants/routes';
import { appReadyAtom } from '@/state';

export type LogoutRouteState = {
  // Another tab ran this logout; this one only follows it and deletes nothing.
  fromBroadcast?: boolean;
  // The user already chose to discard unsynced changes in the waiting modal (use-safe-logout).
  discardConfirmed?: boolean;
  // The local database could not be opened (DB6/COL12 recovery in LocalDataProvider).
  localDataError?: boolean;
};

const LOGOUT_STEP_TIMEOUT_MS = 5_000;
// clearRxDB bounds each of its own steps; this caps the teardown as a whole.
const LOCAL_DATA_TEARDOWN_TIMEOUT_MS = 30_000;

export const Logout = () => {
  const { logout } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectUri = getAuth0RedirectUri();
  const setAppReady = useSetAtom(appReadyAtom);
  const [unsyncedPrompt, setUnsyncedPrompt] = useState<{ unsyncedCount: number | undefined } | null>(null);
  const logoutStartedRef = useRef(false);
  const teardownStartedRef = useRef(false);

  const state: LogoutRouteState = location.state ?? {};

  const clearAndLogout = async () => {
    if (teardownStartedRef.current) return;
    teardownStartedRef.current = true;
    setUnsyncedPrompt(null);

    await runLogoutStep('Resetting analytics', () => {
      capture('user_logged_out');
      reset();
    });

    let localDatabasesDeleted = true;
    if (state.fromBroadcast) {
      // The broadcasting tab owns the deletion; this tab only releases its connections, which would
      // otherwise keep that deletion blocked.
      await runLogoutStep(
        'Closing local data',
        () => localDataService.clearRxDB({ deleteDatabases: false }),
        LOCAL_DATA_TEARDOWN_TIMEOUT_MS
      );
    } else {
      const [, deleted] = await Promise.all([
        runLogoutStep('Broadcasting logout', () => tabSyncService.broadcastLogout()),
        runLogoutStep('Clearing local data', () => localDataService.clearRxDB(), LOCAL_DATA_TEARDOWN_TIMEOUT_MS),
      ]);
      localDatabasesDeleted = deleted === true;
    }

    await runLogoutStep('Clearing editor caches', clearEditorContentCaches);
    await runLogoutStep('Clearing local storage', () => {
      // The pending deletion survives the clear: another tab's logout may have just scheduled it.
      const deletionPending = localStorage.getItem(PENDING_DATABASE_DELETION_KEY) !== null;
      localStorage.clear(); // clear auth
      if (deletionPending || !localDatabasesDeleted) {
        localDataService.scheduleDatabaseDeletionOnNextStart();
      }
    });
    setAppReady(false);

    // Firebase Auth keeps its refresh token in IndexedDB independently of localStorage; without
    // this a logged-out browser could keep minting ID tokens for the previous user.
    await runLogoutStep('Signing out of Firebase', () => signOut(getAuth(getFirebaseApp())));

    if (navigator.onLine) {
      await runLogoutStep('Redirecting through Auth0', () =>
        logout({
          logoutParams: {
            returnTo: redirectUri,
          },
        })
      );
    } else {
      navigate(appRoutes.noConnection, { replace: false });
    }
  };

  useEffect(() => {
    if (logoutStartedRef.current) return;
    logoutStartedRef.current = true;

    // Every route into logout lands here, so this is the one place that guards the deletion: a
    // revoked session and the local-data recovery never pass through use-safe-logout's modal.
    const confirmThenLogout = async () => {
      // Another tab's logout already scheduled the local data for deletion: its user confirmed losing
      // the changes, or had none. Typical for a tab that reloads onto /logout after it, e.g. a frozen
      // leader, which could not otherwise count what it never opened.
      const deletionScheduled = localStorage.getItem(PENDING_DATABASE_DELETION_KEY) !== null;

      if (!state.fromBroadcast && !state.discardConfirmed) {
        const unsyncedCount = await runLogoutStep('Counting unsynced changes', () => localDataService.getUnpushedWriteCount());
        if ((unsyncedCount !== 0 && !deletionScheduled) || state.localDataError) {
          setUnsyncedPrompt({ unsyncedCount });
          return;
        }
      }

      await clearAndLogout();
    };

    void confirmThenLogout();
  }, [logout]);

  return (
    <>
      {!unsyncedPrompt && <Loader fullScreen />}
      {unsyncedPrompt && (
        <UnsyncedChangesLogoutModal
          unsyncedCount={unsyncedPrompt.unsyncedCount}
          localDataError={!!state.localDataError}
          onLogout={() => void clearAndLogout()}
        />
      )}
    </>
  );
};

// A step that throws or never settles degrades to a warning: nothing may keep the full-screen loader
// from reaching the redirect. A failed count reads as undefined, i.e. "cannot tell".
const runLogoutStep = async <T,>(
  label: string,
  step: () => T | Promise<T>,
  timeoutMs = LOGOUT_STEP_TIMEOUT_MS
): Promise<T | undefined> => {
  try {
    return await withTimeout(Promise.resolve().then(step), timeoutMs, label);
  } catch (error) {
    console.warn(`[LOGOUT] ${label} failed:`, error);
    return undefined;
  }
};
